import React, { useState, useEffect, useMemo, useCallback } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import SubmitOrCancel from 'live/components/forms/ui/submit-or-cancel'
import LiveTooltip from 'live/components/ui/tooltip'
import {postConfig, getConfig, getLiveUsers} from '../../api'

import globalNotification from 'live/services/global-notification'
import authorization from 'live/services/authorization'

import {SuperStackStatusConfiguration} from "../../utils/types";
import timestampToDateString from "../../utils/timestampToDateString";

const eventStore = require('live/components/events/lib/store')('config/datasource-form', {
    enforceDependencies: true
})

const eventActions = require('live/components/events/lib/actions')('config/datasource-form')

export default function ConfigStackInfo() {
    const [queryHeader, setQueryHeader] = useState<string>('')
    const [queryDropdown, setQueryDropdown] = useState<string>('')
    const [queryTable, setQueryTable] = useState<string>('')
    const [username, setUsername] = useState<string>()
    const [formState, setFormState] = useState<SuperStackStatusConfiguration>()
    const [config, setConfig] = useState<SuperStackStatusConfiguration>()

    const [queryHeaderProvisioning, setQueryHeaderProvisioning] = useState<string>('')
    const [queryDropdownProvisioning, setQueryDropdownProvisioning] = useState<string>('')
    const [queryTableProvisioning, setQueryTableProvisioning] = useState<string>('')

    const actions = useMemo(() => {
        return {
            ...eventActions,
            setChart: function () {
                return false
            },
            setProvider: provider => {
                if (provider !== 'pipes') {
                    // GlobalNotification.error(<Notification title={i18n(`Can't change provider`)}></Notification>, 5)
                    globalNotification.error(<div title={i18n(`Can't change provider`)}></div>, 5)
                }
            }
        }
    }, [])

    useEffect(() => {
        ;(async () => {
            const fetchedConfig = await getConfig()
            if (fetchedConfig?.success) {
                const user = await getLiveUsers(fetchedConfig.data.updatedBy)
                if (user?.id) setUsername(user.name)
                setConfig(fetchedConfig?.data)
                setFormState(fetchedConfig?.data)
            }
        })()
    }, [])

    const handlePost = useCallback(async (parameters: SuperStackStatusConfiguration) => {
        const response = await postConfig({ ...parameters, updatedAt: null, updatedBy: Number(authorization.user.id) })
        if (response?.success) {
            setConfig(response.data)
            setFormState(response?.data)
            globalNotification.success('Configuração salva com sucesso')
        } else globalNotification.error('Erro ao salvar a configuração')
    }, [])

    const ExpressionPreview = useMemo(() => {
        return require('live/components/forms/ui/expression-preview')(eventStore, actions)
    }, [actions, eventStore])

    return (
        <Container>
            <div className="title-bar">
                <span>{i18n('DADOS ESTÁTICOS')}</span>
                <span>{i18n('Stacks overview: Query pipes configuration')}</span>
            </div>
            <div className="cluster-manager-form-container">
                <div className="cluster-manager-forms">
                    <div className="cluster-manager-fieldset-container">
                        <span className="cluster-manager-fieldset-title">
                            {i18n('Header KPIS')}
                            <LiveTooltip
                                className=""
                                content={i18n("Click on “Write expression” or the edit button to define the query that will define the parameters of the header that contains the stack status indicators.")}
                                position="top"
                            >
                                <i className={"far fa-info-circle"} style={{marginLeft: 4}} > </i>
                            </LiveTooltip>
                        </span>
                        <div className="cluster-manager-fieldset">
                            <ExpressionPreview
                                expressionHasChanged={true}
                                readonly={!true}
                                expressionHasChangedTemplate={<span />}
                                data={{
                                    provider: 'pipes',
                                    expression: formState?.headerConfiguration
                                }}
                                apply={({ expression }) => setFormState({...formState, headerConfiguration: expression})}
                            />
                        </div>
                    </div>
                    <div className="cluster-manager-fieldset-container">
                        <span className="cluster-manager-fieldset-title">
                            {i18n('Customer selector')}
                            <LiveTooltip
                                className=""
                                content={i18n("Click on “Write expression” or the edit button to define the query that will define the customer selector filter.")}
                                position="top"
                            >
                                <i className={"far fa-info-circle"} style={{marginLeft: 4}} > </i>
                            </LiveTooltip>
                        </span>
                        <div className="cluster-manager-fieldset">
                            <ExpressionPreview
                                expressionHasChanged={true}
                                readonly={!true}
                                expressionHasChangedTemplate={<span />}
                                data={{
                                    provider: 'pipes',
                                    expression: formState?.dropdownDataConfiguration
                                }}
                                apply={({ expression }) => setFormState({...formState, dropdownDataConfiguration: expression})}
                            />
                        </div>
                    </div>
                    <div className="cluster-manager-fieldset-container">
                        <span className="cluster-manager-fieldset-title">
                            {i18n('Stack list: Table data')}
                            <LiveTooltip
                                className=""
                                content={i18n("Click on “Write expression” or the edit button to define the query that will define the display of data in the stack list table.")}
                                position="top"
                            >
                                <i className={"far fa-info-circle"} style={{marginLeft: 4}} > </i>
                            </LiveTooltip>
                        </span>
                        <div className="cluster-manager-fieldset">
                            <ExpressionPreview
                                expressionHasChanged={true}
                                readonly={!true}
                                expressionHasChangedTemplate={<span />}
                                data={{
                                    provider: 'pipes',
                                    expression: formState?.tableDataConfiguration
                                }}
                                apply={({ expression }) => setFormState({...formState, tableDataConfiguration: expression})}
                            />
                        </div>
                    </div>
                </div>
                <div className="cluster-manager-last-edit">
                    Última edição:{' '}
                    <span>{config?.updatedAt > 0 ? timestampToDateString(config.updatedAt, true) : '--/--/--'}</span> -
                    por <span>{username}</span>
                </div>
                <div className="sub-cancel-container">
                    <SubmitOrCancel
                        disabled={false}
                        onCancel={() => setFormState(config)}
                        onSubmit={async () => {
                            await handlePost(formState)
                        }}
                        buttonMessage={'Aplicar'}
                        type="button"
                    />
                </div>
            </div>
        </Container>
    )
}

const Container = styled.div`
    background-color: ${({ theme }) => theme['gray-0']};
    height: calc(100% - 36px);
    width: 100%;
    position: relative;
    top: 36px;
    display: flex;
    flex-direction: column;
    gap: 32px;
    padding-bottom: 8px;
    align-items: center;
    z-index: 9;
  
    .title-bar {
        width: 100%;
        height: 36px;
        background-color: ${({ theme }) => theme['gray-200']};
        color: ${({ theme }) => theme['gray-700']};
        font-family: ${({ theme }) => theme['font-semibold']};
        padding-left: 24px;
        display: flex;
        align-items: center;
        gap: 10px;

        span:last-child {
            border-left: ${({ theme }) => `1px solid ${theme['gray-400']}`};
            padding-left: 10px;
        }
    }

    .cluster-manager-form-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;

        .cluster-manager-forms {
            display: flex;
              flex-direction: column;
              width: 70vw;
            gap: 24px;
            .cluster-manager-fieldset-container {
                display: flex;
                flex-direction: column;
                gap: 8px;

                .cluster-manager-fieldset-title {
                    font-size: 14px;
                    color: ${({ theme }) => theme['gray-900']};
                    font-family: ${({ theme }) => theme['font-semibold']};
                }

                .cluster-manager-fieldset {
                    display: flex;
                    flex-direction: column;
                    background-color: ${({ theme }) => theme['gray-0']};
                    min-width: 400px;
                    padding: 24px;
                    border: ${({ theme }) => `1px solid ${theme['gray-500']}`};
                    border-radius: 3px;
                    gap: 16px;

                    .cluster-manager-input-container {
                        display: flex;
                        flex-direction: column;
                        line-height: 16px;
                        gap: 4px;

                        .label {
                            color: ${({ theme }) => theme['gray-900']};
                            font-family: ${({ theme }) => theme['font-semibold']};
                            font-size: 13px;
                        }

                        .info {
                            color: ${({ theme }) => theme['gray-700']};

                            i {
                                font-size: 10px;
                            }

                            font-size: 12px;
                        }
                    }
                }
            }
        }

        .cluster-manager-last-edit {
            width: 100%;
            font-size: 13px;
            font-family: ${({ theme }) => theme['font-regular']};
            color: ${({ theme }) => theme['gray-600']};
        }

        .sub-cancel-container {
            display: flex;
            width: 100%;
            align-items: center;
            justify-content: space-between;
            height: 63px;
            background-color: ${({ theme }) => theme['gray-200']};
            padding-left: 24px;
        }
    }

    .no-arrow-number-input::-webkit-outer-spin-button,
    .no-arrow-number-input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    .no-arrow-number-input[type='number'] {
        -moz-appearance: textfield;
    }
`
